import React, { useState } from 'react';

interface ResetPasswordConfirmProps {
  email: string;
  onSuccess: () => void;
  onCancel: () => void;
}

const ResetPasswordConfirm: React.FC<ResetPasswordConfirmProps> = ({ email, onSuccess, onCancel }) => {
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Adgangskoderne matcher ikke');
      return;
    }

    if (newPassword.length < 10) {
      setError('Adgangskode skal være mindst 10 tegn');
      return;
    }

    if (!/[A-Z]/.test(newPassword)) {
      setError('Adgangskode skal indeholde mindst ét stort bogstav');
      return;
    }

    if (!/[0-9]/.test(newPassword)) {
      setError('Adgangskode skal indeholde mindst ét tal');
      return;
    }

    setLoading(true);

    try {
      const API_URL = process.env.REACT_APP_API_URL ||
        'https://fieldvision-api.politemushroom-acd865dd.northeurope.azurecontainerapps.io';

      const response = await fetch(`${API_URL}/api/auth/reset-password/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          resetCode,
          newPassword
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Ugyldig nulstillingskode');
      }

      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Der opstod en fejl');
    } finally {
      setLoading(false);
    }
  };

  // Handle paste of 6-digit code
  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow digits
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setResetCode(value);
  };

  return (
    <div className="bg-white p-8 rounded-lg shadow-md">
      <h2 className="text-2xl font-bold mb-2 text-center text-gray-800">Indtast ny adgangskode</h2>
      <p className="text-sm text-gray-600 mb-6 text-center">
        Vi har sendt en nulstillingskode til <strong>{email}</strong>
      </p>

      <form onSubmit={handleSubmit} autoComplete="off">
        <div className="mb-4">
          <label
            htmlFor="reset-code"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Nulstillingskode (6 cifre)
          </label>
          <input
            type="text"
            id="reset-code"
            name="reset-code"
            autoComplete="one-time-code"
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            value={resetCode}
            onChange={handleCodeChange}
            placeholder="000000"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-center text-2xl tracking-widest font-mono"
            required
          />
        </div>

        <div className="mb-4">
          <label
            htmlFor="new-password"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Ny adgangskode
          </label>
          <input
            type="password"
            id="new-password"
            name="new-password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Min. 10 tegn, stort bogstav, tal"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            required
          />
        </div>

        <div className="mb-6">
          <label
            htmlFor="confirm-password"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Bekræft ny adgangskode
          </label>
          <input
            type="password"
            id="confirm-password"
            name="confirm-password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            required
          />
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading || resetCode.length !== 6}
          className="w-full py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors mb-3"
        >
          {loading ? 'Nulstiller...' : 'Nulstil adgangskode'}
        </button>

        <button
          type="button"
          onClick={onCancel}
          className="w-full py-3 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-colors"
        >
          Annuller
        </button>
      </form>
    </div>
  );
};

export default ResetPasswordConfirm;
